import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceArea,
} from "recharts";
import { Sun, Snowflake, Thermometer, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * ChargingTodayCard
 * ------------------------------------------------------------------
 * Weather-aware charging guidance. Combines Tesla's live outside-temp
 * telemetry with the vehicle's climate zone + SoH to render:
 *   1. Current outside temp + extreme-heat/cold advisory
 *   2. A short, direct recommendation
 *   3. Optimal charging window for today
 *   4. A 24-hour projected pack-cell temperature curve (mini area chart)
 *
 * Everything is deterministic — no LLM call. Values line up with the
 * "Weather-aware Charging" mock on the marketing site so users see the
 * same intelligence they were promised.
 */
const c2f = (c) => (c == null ? null : Math.round((c * 9) / 5 + 32));

// Zone defaults — hourly ambient bands (°F) used when Tesla telemetry
// hasn't provided a fresh outside_temp reading. These are conservative
// mid-year values for each climate zone; the exact curve refines as
// soon as `outsideTempF` is known.
const ZONE_AMBIENT = {
  "hot-desert":     { min: 78, max: 108, low: 4, high: 15 }, // low/high hour-of-day
  "hot-humid":      { min: 74, max:  96, low: 5, high: 15 },
  "temperate":      { min: 52, max:  78, low: 6, high: 15 },
  "mild-coastal":   { min: 54, max:  72, low: 5, high: 14 },
  "cold-north":     { min: 18, max:  42, low: 6, high: 14 },
  "high-elevation": { min: 34, max:  70, low: 6, high: 15 },
};

function projectAmbient24h(zone, currentF) {
  const z = ZONE_AMBIENT[zone] || ZONE_AMBIENT["temperate"];
  // If we have a live reading, anchor the curve to it at "now" (hour 0).
  // Otherwise sample a sinusoid across the day.
  const nowHour = new Date().getHours();
  const points = [];
  for (let i = 0; i < 24; i++) {
    const hour = (nowHour + i) % 24;
    // Sinusoidal ambient temp across the day, peaking near z.high.
    const phase = ((hour - z.low) / 24) * Math.PI * 2;
    const raw = (z.min + z.max) / 2 + ((z.max - z.min) / 2) * -Math.cos(phase);
    // Cell temp lags ambient by ~2-3°F and stays a touch warmer at night
    // because the pack radiates slowly. Add a small offset.
    const cell = raw + 3 + (hour >= 22 || hour <= 4 ? 2 : 0);
    // If we have a fresh live reading, anchor hour 0 to it and let the
    // curve drift naturally from there.
    if (i === 0 && typeof currentF === "number") {
      const anchor = currentF + 3;
      points.push({ h: i, hour, ambient: currentF, cell: anchor });
      continue;
    }
    points.push({ h: i, hour, ambient: +raw.toFixed(0), cell: +cell.toFixed(0) });
  }
  return points;
}

function buildRecommendation({ outsideTempF, zone, soh }) {
  // Extreme-heat regime — pack chemistry degrades ~3x faster above 100°F
  if (outsideTempF != null && outsideTempF >= 100) {
    return {
      severity: "high",
      icon: Sun,
      color: "#FF6B57",
      title: "Extreme heat advisory",
      body: "Skip Supercharging today. Home L2 overnight only. High-temp DC fast-charging accelerates calendar aging on any pack chemistry.",
    };
  }
  // Extreme cold regime
  if (outsideTempF != null && outsideTempF <= 20) {
    return {
      severity: "medium",
      icon: Snowflake,
      color: "#7FD1FF",
      title: "Deep cold advisory",
      body: "Precondition the pack for 25 minutes before any Supercharger stop. Cold cells refuse fast current until they reach ~68°F.",
    };
  }
  // Warm-but-not-extreme
  if (outsideTempF != null && outsideTempF >= 90) {
    return {
      severity: "medium",
      icon: Thermometer,
      color: "#FFB020",
      title: "Warm charging conditions",
      body: "Any charging is OK today. If Supercharging, park in shade — direct sun raises cell temp another 8–12°F.",
    };
  }
  // Cool-but-not-cold
  if (outsideTempF != null && outsideTempF <= 40) {
    return {
      severity: "info",
      icon: Snowflake,
      color: "#7FD1FF",
      title: "Cool ambient — plan for slower DC charging",
      body: "First 10 minutes of a Supercharger session will taper. Charge at home overnight when you can.",
    };
  }
  // Older / higher-degradation packs
  if (soh != null && soh < 82) {
    return {
      severity: "info",
      icon: AlertTriangle,
      color: "#FFB020",
      title: "Aging pack — prefer AC charging",
      body: "Home L2 (Level 2) charging preserves capacity better than repeated Supercharger use once the pack is under 82% SoH.",
    };
  }
  // All-clear
  return {
    severity: "ok",
    icon: CheckCircle2,
    color: "#39FF14",
    title: "Ideal charging conditions",
    body: "Ambient temperature is in the sweet spot for cell chemistry. Any charging source is fine today.",
  };
}

function pickOptimalWindow({ outsideTempF, zone }) {
  // Hot zones → charge overnight (coolest cells + off-peak rates)
  if ((outsideTempF != null && outsideTempF >= 90) || zone === "hot-desert" || zone === "hot-humid") {
    return { start: "10 PM", end: "6 AM", reason: "Coolest cells + off-peak rates" };
  }
  // Cold zones → mid-morning (cells warmer than dawn but before peak rate)
  if ((outsideTempF != null && outsideTempF <= 32) || zone === "cold-north") {
    return { start: "10 AM", end: "2 PM", reason: "Warmer cells accept DC current faster" };
  }
  // Temperate default → overnight
  return { start: "11 PM", end: "5 AM", reason: "Off-peak utility rates" };
}

export const ChargingTodayCard = ({ vehicle, climate }) => {
  const outsideC = vehicle?.outsideTempC ?? null;
  const outsideF = outsideC != null ? c2f(outsideC) : null;
  const soh = vehicle?.sohCurrent ?? null;
  const zone = climate?.zone || "temperate";

  const advisory = useMemo(
    () => buildRecommendation({ outsideTempF: outsideF, zone, soh }),
    [outsideF, zone, soh]
  );
  const window = useMemo(() => pickOptimalWindow({ outsideTempF: outsideF, zone }), [outsideF, zone]);
  const points = useMemo(() => projectAmbient24h(zone, outsideF), [zone, outsideF]);

  // Highlight the optimal-charging window on the mini chart.
  const startIdx = points.findIndex((p) => p.hour === parseWindowHour(window.start));
  const endIdx = points.findIndex((p) => p.hour === parseWindowHour(window.end));
  const showBand = startIdx !== -1 && endIdx !== -1;

  const AdvIcon = advisory.icon;
  const isExtreme = advisory.severity === "high";

  return (
    <div
      className="rounded-xl border p-4 space-y-3"
      style={{
        borderColor: isExtreme ? `${advisory.color}55` : "rgba(255,255,255,0.10)",
        background: isExtreme
          ? `linear-gradient(180deg, ${advisory.color}0e, transparent 60%)`
          : "rgba(255,255,255,0.02)",
      }}
      data-testid="charging-today-card"
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border"
          style={{ background: `${advisory.color}18`, borderColor: `${advisory.color}55` }}
        >
          <AdvIcon className="w-4 h-4" style={{ color: advisory.color }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            Charging today
          </div>
          <div className="text-sm text-white font-semibold leading-snug mt-0.5" data-testid="charging-advisory-title">
            {advisory.title}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-lg text-white font-semibold leading-none" data-testid="charging-outside-temp">
            {outsideF != null ? `${outsideF}°F` : "—"}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 mt-1">
            outside
          </div>
        </div>
      </div>

      {/* Recommendation body */}
      <p className="text-[12px] text-zinc-300 leading-relaxed" data-testid="charging-advisory-body">
        {advisory.body}
      </p>

      {/* Optimal window pill */}
      <div
        className="flex items-center gap-2 rounded-lg border px-3 py-2"
        style={{
          borderColor: "rgba(0,229,255,0.28)",
          background: "rgba(0,229,255,0.06)",
        }}
        data-testid="charging-optimal-window"
      >
        <Clock className="w-3.5 h-3.5" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-white font-semibold leading-tight">
            Optimal window · <span style={{ color: "#00E5FF" }}>{window.start} – {window.end}</span>
          </div>
          <div className="font-mono text-[9px] text-zinc-400 leading-tight">
            {window.reason}
          </div>
        </div>
      </div>

      {/* 24hr cell temp projection */}
      <div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            Projected cell temp · next 24h
          </span>
          <span className="font-mono text-[10px] text-zinc-500">
            {Math.min(...points.map((p) => p.cell))}°F – {Math.max(...points.map((p) => p.cell))}°F
          </span>
        </div>
        <div className="h-24 -ml-2" data-testid="charging-temp-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={points} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cellTempGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FF6B57" stopOpacity={0.35} />
                  <stop offset="60%" stopColor="#FFB020" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#00E5FF" stopOpacity={0.08} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="hour"
                tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 9, fontFamily: "monospace" }}
                axisLine={false}
                tickLine={false}
                interval={5}
                tickFormatter={(h) => (h % 24 === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`)}
              />
              <YAxis
                domain={["dataMin - 4", "dataMax + 4"]}
                hide
              />
              {showBand && (
                <ReferenceArea
                  x1={points[startIdx].h}
                  x2={points[endIdx].h}
                  fill="#00E5FF"
                  fillOpacity={0.08}
                  strokeOpacity={0}
                />
              )}
              <Tooltip
                cursor={{ stroke: "rgba(255,255,255,0.15)" }}
                contentStyle={{
                  background: "rgba(0,0,0,0.9)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 6,
                  fontSize: 11,
                }}
                labelFormatter={(h) => {
                  const p = points[h];
                  if (!p) return "";
                  const hr = p.hour;
                  return hr === 0 ? "12 AM" : hr < 12 ? `${hr} AM` : hr === 12 ? "12 PM" : `${hr - 12} PM`;
                }}
                formatter={(v, n) => [`${v}°F`, n === "cell" ? "Cell temp" : "Ambient"]}
              />
              <Area
                type="monotone"
                dataKey="cell"
                stroke="#FF6B57"
                strokeWidth={1.6}
                fill="url(#cellTempGrad)"
                dot={false}
              />
              <Area
                type="monotone"
                dataKey="ambient"
                stroke="rgba(0,229,255,0.55)"
                strokeWidth={1.2}
                strokeDasharray="3 3"
                fill="transparent"
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-3 mt-1.5 px-1">
          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] text-zinc-500">
            <span className="w-2 h-0.5" style={{ background: "#FF6B57" }} />
            Cell
          </span>
          <span className="inline-flex items-center gap-1.5 font-mono text-[9px] text-zinc-500">
            <span
              className="w-2 h-0.5"
              style={{
                background: "rgba(0,229,255,0.55)",
                borderTop: "1px dashed rgba(0,229,255,0.7)",
              }}
            />
            Ambient
          </span>
          {showBand && (
            <span className="inline-flex items-center gap-1.5 font-mono text-[9px] text-zinc-500">
              <span className="w-2 h-2" style={{ background: "rgba(0,229,255,0.18)" }} />
              Optimal
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

function parseWindowHour(label) {
  // "10 PM" → 22; "6 AM" → 6; "5 AM" → 5
  const m = /^(\d{1,2})\s*(AM|PM)$/i.exec(label);
  if (!m) return -1;
  let h = parseInt(m[1], 10);
  const pm = m[2].toUpperCase() === "PM";
  if (pm && h < 12) h += 12;
  if (!pm && h === 12) h = 0;
  return h;
}
